import { useState, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CheckCircle2, XCircle, FileCode, Eraser, Copy, Wand2, AlertTriangle, Info, HelpCircle, Wrench, Check, X, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';
import type { AdfElementNode } from '@/hooks/useAdfStandard';
import { generateFixedXml, countFixableErrors, type FixGeneratorResult } from '@/lib/adf';
import { extractLineNumber, getLineContext, truncateLine } from '@/lib/validation-utils';
import { LineNumberedTextarea } from '@/components/shared/LineNumberedTextarea';

interface AdfXmlValidatorProps {
  elementTree: AdfElementNode[];
  exampleXml?: string;
}

interface ValidationError {
  path: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  category?: 'unknown' | 'extension' | 'missing' | 'invalid-attr' | 'suggestion' | 'parse';
  suggestion?: string;
  line?: number;
  column?: number;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

interface ValidatorOptions {
  caseInsensitive?: boolean;
}

/**
 * Known non-standard elements with educational messages
 * Updated for ADF v2.0
 */
const KNOWN_EXTENSIONS: Record<string, { message: string; suggestion?: string }> = {
  'creditapp': {
    message: 'Non-standard element. For credit applications, consider using <customer><comments> or a custom namespace.',
  },
  'creditapplication': {
    message: 'Non-standard element. For credit applications, consider using <customer><comments> or a custom namespace.',
  },
  'ssn': {
    message: 'Non-standard element. Sensitive data should be transmitted through secure, dedicated channels outside of ADF.',
  },
  'socialsecuritynumber': {
    message: 'Non-standard element. Sensitive data should be transmitted through secure, dedicated channels outside of ADF.',
  },
  'employer': {
    message: 'Non-standard element. Employment data is not part of ADF. Consider using <customer><comments> for additional context.',
  },
  'employment': {
    message: 'Non-standard element. Employment data is not part of ADF. Consider using <customer><comments> for additional context.',
  },
  'tradein': {
    message: 'Use <vehicle interest="trade-in"> instead of <tradein> per ADF specification.',
    suggestion: '<vehicle interest="trade-in">',
  },
  'trade-in': {
    message: 'Use <vehicle interest="trade-in"> instead of <trade-in> per ADF specification.',
    suggestion: '<vehicle interest="trade-in">',
  },
  'dealer': {
    message: 'Use <vendor> instead of <dealer> per ADF specification.',
    suggestion: '<vendor>',
  },
  'dealership': {
    message: 'Use <vendor> instead of <dealership> per ADF specification.',
    suggestion: '<vendor>',
  },
  'source': {
    message: 'Use <provider> instead of <source> per ADF specification.',
    suggestion: '<provider>',
  },
  'leadprovider': {
    message: 'Use <provider> instead of <leadprovider> per ADF specification.',
    suggestion: '<provider>',
  },
  'leadsource': {
    message: 'Use <provider> instead of <leadsource> per ADF specification.',
    suggestion: '<provider>',
  },
  'automobile': {
    message: 'Use <vehicle> instead of <automobile> per ADF specification.',
    suggestion: '<vehicle>',
  },
  'car': {
    message: 'Use <vehicle> instead of <car> per ADF specification.',
    suggestion: '<vehicle>',
  },
  'buyer': {
    message: 'Use <customer> instead of <buyer> per ADF specification.',
    suggestion: '<customer>',
  },
  'dob': {
    message: 'Non-standard element. Date of birth should be transmitted through secure channels outside of ADF.',
  },
  'dateofbirth': {
    message: 'Non-standard element. Date of birth should be transmitted through secure channels outside of ADF.',
  },
  'driverslicense': {
    message: 'Non-standard element. License data should be transmitted through secure, dedicated channels outside of ADF.',
  },
  'license': {
    message: 'Non-standard element. License data should be transmitted through secure, dedicated channels outside of ADF.',
  },
  'income': {
    message: 'Non-standard element. Financial data should be transmitted through secure, dedicated channels outside of ADF.',
  },
  'salary': {
    message: 'Non-standard element. Financial data should be transmitted through secure, dedicated channels outside of ADF.',
  },
};

/**
 * Known non-standard attribute values with suggestions
 * These are common industry extensions that should be auto-fixed
 */
const KNOWN_ATTRIBUTE_EXTENSIONS: Record<string, Record<string, { message: string; standardValue: string }>> = {
  type: {
    'mobilephone': {
      message: 'Non-standard phone type. Use "cellphone" instead.',
      standardValue: 'cellphone',
    },
    'mobile': {
      message: 'Non-standard phone type. Use "cellphone" instead.',
      standardValue: 'cellphone',
    },
  },
};

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Find similar element names for "Did you mean?" suggestions
 */
function findSimilarElements(tagName: string, validTags: string[]): string[] {
  const normalized = tagName.toLowerCase();
  return validTags
    .map(tag => ({ tag, distance: levenshteinDistance(normalized, tag.toLowerCase()) }))
    .filter(item => item.distance <= 2 && item.distance > 0) // Max 2 edits, exclude exact matches
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 3)
    .map(item => item.tag);
}

/**
 * Find a child spec from a parent element spec by tag name
 * This enables context-aware validation for elements that appear in multiple places
 */
function findChildSpec(
  parentSpec: AdfElementNode | null,
  tagName: string,
  caseInsensitive: boolean = false
): AdfElementNode | null {
  if (!parentSpec?.children) return null;
  
  const tagLower = tagName.toLowerCase();
  return parentSpec.children.find(child => {
    const childTagLower = child.tag_name.toLowerCase();
    return caseInsensitive ? childTagLower === tagLower : child.tag_name === tagName;
  }) || null;
}

/**
 * Validates ADF XML structure against element tree.
 * Uses context-aware validation: looks up child specs from parent context
 * to correctly validate elements like vendor/contact vs customer/contact.
 */
function validateAdfXml(
  xmlString: string,
  elementTree: AdfElementNode[],
  options: ValidatorOptions = {}
): ValidationResult {
  const errors: ValidationError[] = [];
  const { caseInsensitive = false } = options;

  // Try to parse XML
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'application/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    const errorMessage = parseError.textContent?.split('\n')[0] || 'XML parsing error';
    const lineNumber = extractLineNumber(errorMessage);
    return {
      valid: false,
      errors: [{
        path: '(root)',
        message: errorMessage,
        type: 'error',
        category: 'parse',
        line: lineNumber ?? undefined,
      }],
    };
  }

  // Collect all tag names for fuzzy matching suggestions
  const allTagNames: string[] = [];
  const collectTagNames = (nodes: AdfElementNode[]) => {
    nodes.forEach((node) => {
      if (!allTagNames.includes(node.tag_name)) {
        allTagNames.push(node.tag_name);
      }
      if (node.children) collectTagNames(node.children);
    });
  };
  collectTagNames(elementTree);

  // Helper to normalize tag name for comparison
  const normalizeTag = (tag: string): string => {
    return caseInsensitive ? tag.toLowerCase() : tag;
  };

  // Check for <adf> root element
  const rootElement = doc.documentElement;
  const rootTagNormalized = normalizeTag(rootElement.tagName);
  if (rootTagNormalized !== 'adf') {
    errors.push({
      path: '/',
      message: `Root element must be <adf>, found <${rootElement.tagName}>`,
      type: 'error',
      category: 'unknown',
    });
    return { valid: false, errors };
  }

  // Find the adf root spec
  const adfSpec = elementTree.find(el => el.tag_name.toLowerCase() === 'adf') || null;

  /**
   * Validate element structure recursively with parent context.
   * @param element - The DOM element to validate
   * @param path - The path string for error reporting
   * @param parentSpec - The parent element's spec (for context-aware child lookup)
   */
  const validateElement = (
    element: Element, 
    path: string, 
    parentSpec: AdfElementNode | null
  ) => {
    const tagName = element.tagName;
    const tagNameLower = tagName.toLowerCase();
    
    // Look up spec from parent context first (context-aware validation)
    let spec: AdfElementNode | null = null;
    
    if (parentSpec) {
      // Find this element as a child of the parent
      spec = findChildSpec(parentSpec, tagName, caseInsensitive);
    } else {
      // Root level - use adf spec
      spec = tagNameLower === 'adf' ? adfSpec : null;
    }

    if (!spec) {
      // Check if it's a known extension
      const knownExt = KNOWN_EXTENSIONS[tagNameLower];
      if (knownExt) {
        errors.push({
          path,
          message: knownExt.message,
          type: 'warning',
          category: 'extension',
          suggestion: knownExt.suggestion,
        });
      } else {
        // Check for similar elements ("Did you mean?")
        const similar = findSimilarElements(tagName, allTagNames);
        if (similar.length > 0) {
          errors.push({
            path,
            message: `Unknown element <${tagName}>. Did you mean: ${similar.map(s => `<${s}>`).join(', ')}?`,
            type: 'warning',
            category: 'suggestion',
            suggestion: similar[0],
          });
        } else {
          errors.push({
            path,
            message: `Unknown element <${tagName}>`,
            type: 'warning',
            category: 'unknown',
          });
        }
      }
      // Still recurse into children for unknown elements (no spec context)
      Array.from(element.children).forEach((child) => {
        validateElement(child, `${path}/${child.tagName.toLowerCase()}`, null);
      });
      return;
    }

    // Check case mismatch warning (only when case-insensitive is enabled)
    if (caseInsensitive && tagName !== spec.tag_name && tagNameLower === spec.tag_name.toLowerCase()) {
      errors.push({
        path,
        message: `Case mismatch: <${tagName}> should be <${spec.tag_name}>`,
        type: 'info',
        category: 'suggestion',
        suggestion: spec.tag_name,
      });
    }

    // Check for required children
    if (spec.children && spec.children.length > 0) {
      spec.children.forEach((childSpec) => {
        if (childSpec.is_required) {
          let hasChild = false;
          if (caseInsensitive) {
            hasChild = Array.from(element.children).some(
              c => c.tagName.toLowerCase() === childSpec.tag_name.toLowerCase()
            );
          } else {
            hasChild = Array.from(element.children).some(
              c => c.tagName === childSpec.tag_name
            );
          }
          
          if (!hasChild) {
            errors.push({
              path: `${path}/${childSpec.tag_name}`,
              message: `Required element <${childSpec.tag_name}> is missing`,
              type: 'error',
              category: 'missing',
            });
          }
        }
      });
    }

    // Validate attribute values if spec has attributes
    const attrSpec = spec.attributes as Record<string, { values?: string[] }> | null;
    if (attrSpec) {
      Object.entries(attrSpec).forEach(([attrName, config]) => {
        const attrValue = element.getAttribute(attrName);
        if (attrValue && config.values && config.values.length > 0) {
          const isValidValue = caseInsensitive
            ? config.values.some(v => v.toLowerCase() === attrValue.toLowerCase())
            : config.values.includes(attrValue);
          
          if (!isValidValue) {
            // Check if it's a known attribute extension with a suggestion
            const attrExtensions = KNOWN_ATTRIBUTE_EXTENSIONS[attrName.toLowerCase()];
            const extInfo = attrExtensions?.[attrValue.toLowerCase()];
            
            if (extInfo) {
              // Known extension - provide helpful warning with auto-fix suggestion
              errors.push({
                path: `${path}/@${attrName}`,
                message: `${extInfo.message} Use "${extInfo.standardValue}" instead.`,
                type: 'error',
                category: 'invalid-attr',
                suggestion: extInfo.standardValue,
              });
            } else {
              // Unknown invalid value
              errors.push({
                path: `${path}/@${attrName}`,
                message: `Invalid attribute value "${attrValue}". Expected: ${config.values.join(', ')}`,
                type: 'error',
                category: 'invalid-attr',
              });
            }
          }
        }
      });
    }

    // Recurse into children WITH parent context (pass current spec as parent)
    Array.from(element.children).forEach((child) => {
      validateElement(child, `${path}/${child.tagName.toLowerCase()}`, spec);
    });
  };

  // Start validation with adf root element (null parent since adf is the root)
  validateElement(rootElement, '/adf', null);

  return {
    valid: errors.filter((e) => e.type === 'error').length === 0,
    errors,
  };
}

export function AdfXmlValidator({ elementTree, exampleXml }: AdfXmlValidatorProps) {
  const [xmlInput, setXmlInput] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [fixResult, setFixResult] = useState<FixGeneratorResult | null>(null);
  const [showFix, setShowFix] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const { toast } = useToast();
  const { trackAdfValidation } = useAnalytics();
  const resultRef = useRef<HTMLDivElement>(null);

  const scrollToResult = useCallback(() => {
    setIsHighlighted(true);
    setTimeout(() => setIsHighlighted(false), 1500);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, []);

  const validateXml = () => {
    if (!xmlInput.trim()) {
      toast({
        title: 'No XML provided',
        description: 'Please paste XML data to validate.',
        variant: 'destructive',
      });
      return;
    }

    const result = validateAdfXml(xmlInput, elementTree, { caseInsensitive });
    setValidationResult(result);
    setFixResult(null);
    setShowFix(false);
    
    // Track validation
    const errorCount = result.errors.filter(e => e.type === 'error').length;
    trackAdfValidation(result.valid, errorCount);
    
    scrollToResult();
  };

  const generateFix = useCallback(() => {
    if (!validationResult || validationResult.errors.length === 0) return;
    
    const result = generateFixedXml(xmlInput, validationResult.errors, elementTree);
    setFixResult(result);
    setShowFix(true);
    
    toast({
      title: result.allFixed 
        ? 'All issues auto-fixed!' 
        : `${result.appliedFixes.length} fix${result.appliedFixes.length !== 1 ? 'es' : ''} applied`,
      description: result.unfixableErrors.length > 0 
        ? `${result.unfixableErrors.length} issue${result.unfixableErrors.length !== 1 ? 's' : ''} require manual review`
        : undefined,
    });
  }, [validationResult, xmlInput, elementTree, toast]);

  const applyFix = useCallback(() => {
    if (!fixResult) return;
    
    setXmlInput(fixResult.fixedXml);
    setShowFix(false);
    
    // Re-validate with fixed XML
    const result = validateAdfXml(fixResult.fixedXml, elementTree, { caseInsensitive });
    setValidationResult(result);
    setFixResult(null);
    
    toast({ title: 'Fixed XML applied' });
  }, [fixResult, elementTree, caseInsensitive, toast]);

  const copyFixedXml = useCallback(() => {
    if (!fixResult) return;
    navigator.clipboard.writeText(fixResult.fixedXml);
    toast({ title: 'Fixed XML copied to clipboard' });
  }, [fixResult, toast]);

  const loadExample = () => {
    if (!exampleXml) {
      toast({
        title: 'No example available',
        description: 'Please wait for the ADF structure to load.',
        variant: 'destructive',
      });
      return;
    }
    
    setXmlInput(exampleXml);
    const result = validateAdfXml(exampleXml, elementTree, { caseInsensitive });
    setValidationResult(result);
    toast({ title: 'Example loaded & validated' });
    scrollToResult();
  };

  const formatXml = () => {
    try {
      // Simple XML formatting
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlInput, 'application/xml');
      const parseError = doc.querySelector('parsererror');
      if (parseError) throw new Error('Invalid XML');
      
      const serializer = new XMLSerializer();
      let formatted = serializer.serializeToString(doc);
      
      // Basic pretty print
      formatted = formatted
        .replace(/></g, '>\n<')
        .replace(/>\s*</g, '>\n<');
      
      setXmlInput(formatted);
      toast({ title: 'XML formatted' });
    } catch {
      toast({
        title: 'Cannot format',
        description: 'Invalid XML syntax',
        variant: 'destructive',
      });
    }
  };

  const clearInput = () => {
    setXmlInput('');
    setValidationResult(null);
    setFixResult(null);
    setShowFix(false);
    setIsHighlighted(false);
  };

  // Categorize errors for summary
  const errorSummary = useMemo(() => {
    if (!validationResult) return null;
    
    const errors = validationResult.errors.filter(e => e.type === 'error');
    const warnings = validationResult.errors.filter(e => e.type === 'warning');
    const infos = validationResult.errors.filter(e => e.type === 'info');
    const extensions = validationResult.errors.filter(e => e.category === 'extension');
    const suggestions = validationResult.errors.filter(e => e.category === 'suggestion');
    const fixableCount = countFixableErrors(validationResult.errors);
    
    return {
      errorCount: errors.length,
      warningCount: warnings.length,
      infoCount: infos.length,
      extensionCount: extensions.length,
      suggestionCount: suggestions.length,
      hasNonStandard: extensions.length > 0 || suggestions.length > 0,
      fixableCount,
      hasFixableErrors: fixableCount > 0,
    };
  }, [validationResult]);

  const copyReport = () => {
    if (!validationResult) return;

    let report = `ADF Validation Report\n`;
    report += `${'='.repeat(40)}\n\n`;
    report += `Options: Case-insensitive: ${caseInsensitive ? 'Yes' : 'No'}\n\n`;

    if (validationResult.valid) {
      report += '✅ VALID - XML conforms to ADF structure.\n';
      if (errorSummary?.warningCount || errorSummary?.infoCount) {
        report += `\nNotes: ${errorSummary.warningCount} warning(s), ${errorSummary.infoCount} suggestion(s)\n`;
      }
    } else {
      report += `❌ INVALID - ${errorSummary?.errorCount} error(s), ${errorSummary?.warningCount} warning(s) found:\n\n`;
    }

    if (validationResult.errors.length > 0) {
      report += '\nDetails:\n';
      report += `${'-'.repeat(40)}\n`;
      validationResult.errors.forEach((err, i) => {
        const icon = err.type === 'error' ? '❌' : err.type === 'warning' ? '⚠️' : 'ℹ️';
        const lineInfo = err.line ? ` [Line ${err.line}]` : '';
        report += `${i + 1}. ${icon} [${err.type.toUpperCase()}]${lineInfo} Path: ${err.path}\n`;
        report += `   ${err.message}\n`;
        
        // Add line context to report
        if (err.line) {
          const ctx = getLineContext(xmlInput, err.line, 1);
          if (ctx) {
            report += `   Context:\n`;
            ctx.before.forEach(b => {
              report += `     ${b.lineNumber.toString().padStart(4)} | ${truncateLine(b.content, 60)}\n`;
            });
            report += `   > ${ctx.lineNumber.toString().padStart(4)} | ${truncateLine(ctx.targetLine, 60)}\n`;
            ctx.after.forEach(a => {
              report += `     ${a.lineNumber.toString().padStart(4)} | ${truncateLine(a.content, 60)}\n`;
            });
          }
        }
        
        if (err.suggestion) {
          report += `   💡 Suggestion: Use ${err.suggestion}\n`;
        }
        report += '\n';
      });
    }

    navigator.clipboard.writeText(report);
    toast({ title: 'Report copied to clipboard' });
  };

  const getErrorIcon = (type: ValidationError['type']) => {
    switch (type) {
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
    }
  };

  const getErrorBadgeVariant = (type: ValidationError['type']) => {
    switch (type) {
      case 'error':
        return 'destructive' as const;
      case 'warning':
        return 'outline' as const;
      case 'info':
        return 'secondary' as const;
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs sm:text-sm text-muted-foreground">
        Paste your ADF XML below to validate it against the specification structure.
      </p>

      {/* Compute error lines for highlighting */}
      {(() => {
        const errorLines = validationResult?.errors
          .filter(e => e.line)
          .map(e => e.line!) || [];
        return (
          <LineNumberedTextarea
            value={xmlInput}
            onChange={setXmlInput}
            placeholder={'<?xml version="1.0"?>\n<adf>\n  <prospect>\n    ...\n  </prospect>\n</adf>'}
            errorLines={errorLines}
            className="min-h-[180px] md:min-h-[250px]"
          />
        );
      })()}

      {/* Options row */}
      <div className="flex items-center gap-4 py-2 px-3 bg-muted/50 rounded-lg">
        <TooltipProvider>
          <div className="flex items-center gap-2">
            <Switch
              id="case-insensitive"
              checked={caseInsensitive}
              onCheckedChange={setCaseInsensitive}
            />
            <Label htmlFor="case-insensitive" className="text-xs sm:text-sm cursor-pointer">
              Case-insensitive
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[250px]">
                <p className="text-xs">
                  Enable to accept mixed-case tags like &lt;Customer&gt; as valid. 
                  Case mismatches will be noted as suggestions.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={loadExample} variant="outline" size="sm">
          <FileCode className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Load Example</span>
        </Button>
        <Button onClick={formatXml} variant="outline" size="sm" disabled={!xmlInput}>
          <Wand2 className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Format</span>
        </Button>
        <Button onClick={clearInput} variant="outline" size="sm" disabled={!xmlInput}>
          <Eraser className="h-4 w-4" />
          <span className="hidden sm:inline ml-2">Clear</span>
        </Button>
        <div className="flex-1 min-w-0" />
        <Button onClick={validateXml} disabled={!xmlInput} className="shrink-0">
          <span className="hidden sm:inline">Validate XML</span>
          <span className="sm:hidden">Validate</span>
        </Button>
      </div>

      <div
        ref={resultRef}
        className={cn(
          'transition-all duration-300 rounded-lg',
          isHighlighted && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
        )}
      >
        {validationResult && (
          <div className="space-y-3 animate-fade-in">
            {validationResult.valid ? (
              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                <AlertTitle className="text-green-600">Valid!</AlertTitle>
                <AlertDescription className="text-green-600/80 text-sm">
                  Your XML conforms to the ADF specification structure.
                  {errorSummary && (errorSummary.warningCount > 0 || errorSummary.infoCount > 0) && (
                    <span className="block mt-1">
                      {errorSummary.warningCount > 0 && `${errorSummary.warningCount} warning${errorSummary.warningCount !== 1 ? 's' : ''}`}
                      {errorSummary.warningCount > 0 && errorSummary.infoCount > 0 && ', '}
                      {errorSummary.infoCount > 0 && `${errorSummary.infoCount} suggestion${errorSummary.infoCount !== 1 ? 's' : ''}`}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <XCircle className="h-5 w-5 shrink-0" />
                <AlertTitle>
                  {errorSummary?.errorCount} Error{errorSummary?.errorCount !== 1 ? 's' : ''}
                  {errorSummary && errorSummary.warningCount > 0 && `, ${errorSummary.warningCount} Warning${errorSummary.warningCount !== 1 ? 's' : ''}`}
                  {errorSummary?.hasFixableErrors && (
                    <span className="text-sm font-normal ml-2">
                      ({errorSummary.fixableCount} auto-fixable)
                    </span>
                  )}
                </AlertTitle>
                <AlertDescription className="flex items-center gap-2 mt-1">
                  <span>See details below</span>
                  {errorSummary?.hasFixableErrors && !showFix && (
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      onClick={generateFix}
                      className="h-7 text-xs"
                    >
                      <Wrench className="h-3.5 w-3.5 mr-1" />
                      View Recommended Fix
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Recommended Fix Panel */}
            {showFix && fixResult && (
              <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-primary" />
                    Recommended Fix
                    {fixResult.allFixed ? (
                      <Badge variant="default" className="bg-green-600">All Fixed</Badge>
                    ) : (
                      <Badge variant="secondary">
                        {fixResult.appliedFixes.length} fix{fixResult.appliedFixes.length !== 1 ? 'es' : ''}
                      </Badge>
                    )}
                  </h4>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copyFixedXml}>
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      Copy
                    </Button>
                    <Button size="sm" onClick={applyFix}>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Apply
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowFix(false)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Show applied fixes */}
                {fixResult.appliedFixes.length > 0 && (
                  <div className="space-y-1">
                    <h5 className="text-xs font-medium text-muted-foreground">Applied Fixes:</h5>
                    <ul className="text-xs space-y-1">
                      {fixResult.appliedFixes.map((fix, i) => (
                        <li key={i} className="flex items-center gap-2 text-green-600">
                          <CheckCircle2 className="h-3 w-3 shrink-0" />
                          <span className="font-mono">{fix.original}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="font-mono font-medium">{fix.fixed}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Show unfixable errors */}
                {fixResult.unfixableErrors.length > 0 && (
                  <div className="space-y-1">
                    <h5 className="text-xs font-medium text-yellow-600">Manual Review Required:</h5>
                    <ul className="text-xs space-y-1">
                      {fixResult.unfixableErrors.map((err, i) => (
                        <li key={i} className="flex items-start gap-2 text-yellow-600">
                          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                          <div>
                            <span className="font-mono">{err.path}</span>
                            <p className="text-muted-foreground">{err.reason}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Fixed XML preview */}
                <Tabs defaultValue="preview" className="w-full">
                  <TabsList className="h-8">
                    <TabsTrigger value="preview" className="text-xs h-7">Fixed XML</TabsTrigger>
                    <TabsTrigger value="compare" className="text-xs h-7">Compare</TabsTrigger>
                  </TabsList>
                  <TabsContent value="preview" className="mt-2">
                    <pre className="text-xs font-mono bg-muted p-3 rounded overflow-x-auto max-h-[300px] whitespace-pre-wrap break-words">
                      {fixResult.fixedXml}
                    </pre>
                  </TabsContent>
                  <TabsContent value="compare" className="mt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <h6 className="text-xs font-medium text-muted-foreground mb-1">Original</h6>
                        <pre className="text-xs font-mono bg-destructive/10 p-3 rounded overflow-x-auto max-h-[200px] whitespace-pre-wrap break-words">
                          {xmlInput}
                        </pre>
                      </div>
                      <div>
                        <h6 className="text-xs font-medium text-muted-foreground mb-1">Fixed</h6>
                        <pre className="text-xs font-mono bg-green-500/10 p-3 rounded overflow-x-auto max-h-[200px] whitespace-pre-wrap break-words">
                          {fixResult.fixedXml}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}

            {/* Non-standard elements summary banner */}
            {errorSummary?.hasNonStandard && (
              <Alert className="border-yellow-500/50 bg-yellow-500/10">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                <AlertTitle className="text-yellow-700">Non-Standard Elements Detected</AlertTitle>
                <AlertDescription className="text-yellow-600/90 text-sm">
                  {errorSummary.extensionCount > 0 && (
                    <span>{errorSummary.extensionCount} known extension{errorSummary.extensionCount !== 1 ? 's' : ''}</span>
                  )}
                  {errorSummary.extensionCount > 0 && errorSummary.suggestionCount > 0 && ' and '}
                  {errorSummary.suggestionCount > 0 && (
                    <span>{errorSummary.suggestionCount} element{errorSummary.suggestionCount !== 1 ? 's' : ''} with suggestions</span>
                  )}
                  {' — see ADF 1.0 spec for compliant alternatives.'}
                </AlertDescription>
              </Alert>
            )}

            {/* Error list */}
            {validationResult.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">Details</h4>
                <ul className="space-y-2 text-xs sm:text-sm">
                  {validationResult.errors.map((err, i) => {
                    // Get line context if line number is available
                    const lineContext = err.line ? getLineContext(xmlInput, err.line, 1) : null;
                    
                    return (
                      <li
                        key={i}
                        className={cn(
                          'font-mono rounded p-3 break-words',
                          err.type === 'error' && 'bg-destructive/10 border border-destructive/20',
                          err.type === 'warning' && 'bg-yellow-500/10 border border-yellow-500/20',
                          err.type === 'info' && 'bg-blue-500/10 border border-blue-500/20'
                        )}
                      >
                        <div className="flex items-start gap-2">
                          {getErrorIcon(err.type)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-semibold break-all">{err.path}</span>
                              {err.line && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">
                                  Line {err.line}
                                </Badge>
                              )}
                              <Badge variant={getErrorBadgeVariant(err.type)} className="text-[10px] px-1.5 py-0">
                                {err.type}
                              </Badge>
                              {err.category && err.category !== 'unknown' && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  {err.category}
                                </Badge>
                              )}
                            </div>
                            <p className="text-foreground/80">{err.message}</p>
                            
                            {/* Line context preview */}
                            {lineContext && (
                              <div className="mt-2 bg-muted/50 rounded p-2 text-[11px] overflow-x-auto">
                                <div className="text-muted-foreground text-[10px] mb-1">Context:</div>
                                {lineContext.before.map((ctx) => (
                                  <div key={ctx.lineNumber} className="flex gap-2 text-muted-foreground">
                                    <span className="w-6 text-right shrink-0 opacity-60">{ctx.lineNumber}</span>
                                    <span className="whitespace-pre">{truncateLine(ctx.content, 80)}</span>
                                  </div>
                                ))}
                                <div className="flex gap-2 text-destructive font-semibold bg-destructive/10 -mx-2 px-2 py-0.5 rounded">
                                  <span className="w-6 text-right shrink-0">&gt; {lineContext.lineNumber}</span>
                                  <span className="whitespace-pre">{truncateLine(lineContext.targetLine, 80)}</span>
                                </div>
                                {lineContext.after.map((ctx) => (
                                  <div key={ctx.lineNumber} className="flex gap-2 text-muted-foreground">
                                    <span className="w-6 text-right shrink-0 opacity-60">{ctx.lineNumber}</span>
                                    <span className="whitespace-pre">{truncateLine(ctx.content, 80)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {err.suggestion && (
                              <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                                <span className="text-primary">💡</span>
                                <span>Use: <code className="bg-muted px-1 rounded">{err.suggestion}</code></span>
                              </p>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={copyReport}>
              <Copy className="h-4 w-4" />
              <span className="ml-2">Copy Report</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
